import { useEffect } from 'react';

interface SeoJsonLdProps {
  type: 'home' | 'seasonal-booking' | 'orders';
}

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const schemas: Record<string, object[]> = {
  home: [
    {
      '@context': 'https://schema.org',
      '@type': 'Florist',
      name: 'Monceau Fleurs 蒙梭花卉',
      alternateName: '羽天國際股份有限公司',
      url: SITE_URL,
      logo: 'https://static.readdy.ai/image/4f3f06d226654fda02a3ba64a379f0f2/6d800fcef43fbe4ae9892fc829f784b7.jpeg',
      image: 'https://static.readdy.ai/image/4f3f06d226654fda02a3ba64a379f0f2/6d800fcef43fbe4ae9892fc829f784b7.jpeg',
      description:
        'Monceau Fleurs 1965年創立於法國巴黎，由羽天國際股份有限公司在台灣代理營運。提供精緻花束、永生花、婚禮花藝設計、節日花禮預約服務。',
      foundingDate: '1965',
      foundingLocation: 'Paris, France',
      priceRange: '$$-$$$',
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: [
            'Monday',
            'Tuesday',
            'Wednesday',
            'Thursday',
            'Friday',
            'Saturday',
            'Sunday',
          ],
          opens: '11:00',
          closes: '21:30',
        },
      ],
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'Customer Service',
        telephone: '+886-916-333-313',
        email: 'raberlai@gmail.com',
        availableLanguage: ['Chinese', 'English'],
      },
      location: [
        {
          '@type': 'LocalBusiness',
          name: 'Monceau Fleurs 新光三越台北南西店',
          address: {
            '@type': 'PostalAddress',
            streetAddress: '南京西路12號B2',
            addressLocality: '中山區',
            addressRegion: '台北市',
            postalCode: '10491',
            addressCountry: 'TW',
          },
          telephone: '+886-2-2541-9859',
          openingHours: 'Mo-Su 11:00-21:30',
        },
        {
          '@type': 'LocalBusiness',
          name: 'Monceau Fleurs 南港店',
          address: {
            '@type': 'PostalAddress',
            streetAddress: '經貿二路131號1樓',
            addressLocality: '南港區',
            addressRegion: '台北市',
            postalCode: '115',
            addressCountry: 'TW',
          },
          telephone: '+886-2-2654-7976',
          openingHours: 'Mo-Su 11:00-21:30',
        },
      ],
      sameAs: ['https://www.instagram.com/monceautaiwan/'],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: '如何下單購買花禮？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '您可以直接在網站上瀏覽我們的花禮商品，選擇喜歡的款式後點擊「加入購物車」，填寫收件人資訊和配送時間，選擇付款方式後即可完成訂購。我們提供線上刷卡、ATM 轉帳等多種付款方式，訂單確認後會立即開始製作。',
          },
        },
        {
          '@type': 'Question',
          name: '有哪些付款方式？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '我們提供多元的付款方式，包括：信用卡線上刷卡、ATM 轉帳、超商代碼繳費。所有付款方式都經過加密保護，確保您的交易安全。',
          },
        },
        {
          '@type': 'Question',
          name: '配送範圍包含哪些地區？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '我們提供全台灣本島配送服務，包含台北、新北、桃園、台中、台南、高雄等主要城市，以及其他縣市鄉鎮地區。離島地區需另外洽詢客服評估。',
          },
        },
        {
          '@type': 'Question',
          name: '節日花禮需要提前多久預約？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '建議至少提前 3-5 天預約。熱門節日如母親節、情人節、聖誕節等，建議提前 7-14 天預約，以確保我們有充足時間準備最新鮮的花材並完成精緻的設計。',
          },
        },
        {
          '@type': 'Question',
          name: '如何獲得會員點數？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '會員點數獲得方式包括：新會員註冊即贈 100 點；每消費 1 元可累積 1 點；生日當月贈送 200 點；參加官網活動或推薦好友註冊可獲得額外點數。',
          },
        },
        {
          '@type': 'Question',
          name: '點數如何使用？有效期限多久？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '會員點數可在結帳時直接折抵現金使用，1 點 = 1 元，每筆訂單最多可使用訂單金額的 30%。點數自獲得日起有效期限為 1 年，即將到期的點數會在會員中心顯示提醒。',
          },
        },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'Monceau Fleurs 蒙梭花卉',
      url: SITE_URL,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${SITE_URL}/?q={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    },
  ],
  'seasonal-booking': [
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: '節日花禮預約服務 - Monceau Fleurs 蒙梭花卉',
      description:
        '提前預約節日花禮，包含農曆新年、情人節、母親節、畢業季、七夕、聖誕節等各大節日的精緻法式花藝設計。',
      provider: {
        '@type': 'Florist',
        name: 'Monceau Fleurs 蒙梭花卉',
        url: SITE_URL,
      },
      areaServed: {
        '@type': 'Country',
        name: 'Taiwan',
      },
      url: `${SITE_URL}/seasonal-booking`,
      serviceType: '節日花禮預約',
      offers: {
        '@type': 'Offer',
        priceCurrency: 'TWD',
        priceRange: 'NT$1,000 - NT$8,000+',
        availability: 'https://schema.org/InStock',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: '首頁',
          item: SITE_URL,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: '節日花禮預約',
          item: `${SITE_URL}/seasonal-booking`,
        },
      ],
    },
  ],
  orders: [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: '訂單查詢 - Monceau Fleurs 蒙梭花卉',
      description: '查詢您在 Monceau Fleurs 蒙梭花卉的訂單記錄與付款狀態。',
      url: `${SITE_URL}/orders`,
      isPartOf: {
        '@type': 'WebSite',
        name: 'Monceau Fleurs 蒙梭花卉',
        url: SITE_URL,
      },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: '首頁',
            item: SITE_URL,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: '訂單查詢',
            item: `${SITE_URL}/orders`,
          },
        ],
      },
    },
  ],
};

export default function SeoJsonLd({ type }: SeoJsonLdProps) {
  const data = schemas[type] ?? [];

  useEffect(() => {
    const ids: string[] = [];

    data.forEach((schema, index) => {
      const id = `seo-jsonld-${type}-${index}`;
      ids.push(id);

      let el = document.getElementById(id) as HTMLScriptElement | null;
      if (!el) {
        el = document.createElement('script');
        el.id = id;
        el.type = 'application/ld+json';
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(schema);
    });

    return () => {
      ids.forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, [type]);

  return null;
}
